import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { Eyebrow } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  admin: "Admin",
  city_official: "Social Equity Department",
  client: "Applicant",
};

const STATUS_STYLES = {
  pending:  { label: "Pending",  cls: "bg-warm text-[#c47a12] border-[#f3d9b0]" },
  opened:   { label: "Opened",   cls: "bg-[#e8eef7] text-[#1f5f8b] border-[#c9d8ea]" },
  accepted: { label: "Accepted", cls: "bg-mint text-equal-dark border-[#bfe5d1]" },
  already_member:  { label: "Already Member",  cls: "bg-page text-mute border-line" },
  already_invited: { label: "Already Invited", cls: "bg-page text-mute border-line" },
};

export default function InvitesPage() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [role, setRole] = useState("city_official");
  const [resending, setResending] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("role");

  const fetchInvites = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/invites`, { headers: { Authorization: `Bearer ${token}` } });
      setInvites(res.data.invites || []);
    } catch { toast.error("Failed to load invites"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const handleSend = async (e) => {
    e.preventDefault();
    const emails = emailInput.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    if (!emails.length) { toast.error("Enter at least one email"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/invites`,
        { emails, role },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const results = res.data.results || [];
      const sent = results.filter(r => r.status === "invited").length;
      const already = results.filter(r => r.status !== "invited").length;
      if (sent > 0) toast.success(`${sent} invite${sent > 1 ? "s" : ""} sent!`);
      if (already > 0) toast.info(`${already} skipped — already member or invited`);
      setEmailInput("");
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invites");
    } finally { setSending(false); }
  };

  const handleResend = async (inviteId, email) => {
    setResending(inviteId);
    try {
      await axios.post(`${API}/admin/invites/${inviteId}/resend`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Invite resent to ${email}`);
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend");
    } finally { setResending(null); }
  };

  const handleDelete = async (inviteId, email) => {
    if (!window.confirm(`Cancel invite for ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/invites/${inviteId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Invite cancelled");
      fetchInvites();
    } catch { toast.error("Failed to cancel"); }
  };

  const stats = useMemo(() => ({
    total: invites.length,
    pending: invites.filter(i => i.status === "pending").length,
    opened: invites.filter(i => i.status === "opened").length,
    accepted: invites.filter(i => i.status === "accepted").length,
  }), [invites]);

  const sortedInvites = useMemo(() =>
    [...invites].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
  [invites]);

  return (
    <AdminShell active="invites">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(userRole === "admin" ? "/admin" : "/city")}
          className="btn-quiet btn-sm -ml-3">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
          </svg>
          Back
        </button>
      </div>

      <div className="max-w-5xl space-y-10">

        {/* Header */}
        <div>
          <Eyebrow className="mb-3">Team Management</Eyebrow>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-ink tracking-tight leading-[1.05]">
            Invitations
          </h1>
          <p className="text-mute mt-4">Invite Social Equity Department staff and applicants to access Equal.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Total Sent", value: stats.total },
            { label: "Pending", value: stats.pending, color: "#c47a12" },
            { label: "Opened", value: stats.opened, color: "#1f5f8b" },
            { label: "Accepted", value: stats.accepted, color: "#0f8a5a" },
          ].map((s) => (
            <div key={s.label} className="card-flat p-5">
              <Eyebrow className="mb-2">{s.label}</Eyebrow>
              <p className="text-3xl font-extrabold tracking-tight tabular-nums" style={{ color: s.color || "#0e1b2a" }}>
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {/* Send invites form */}
        <div className="card-flat p-6">
          <p className="text-base font-bold text-ink tracking-tight mb-4">
            Send Invitations
          </p>
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <label className="block th-mono mb-2">
                Email addresses
              </label>
              <textarea
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                placeholder={"evelyn.amado@longbeach.gov\njohn.smith@longbeach.gov\n..."}
                rows={4}
                className="input-flat h-auto px-4 py-3 resize-none"
                data-testid="invite-email-input"
              />
              <p className="text-xs text-mute mt-1">One email per line, or separate with commas</p>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block th-mono mb-2">Role</label>
                <select value={role} onChange={e => setRole(e.target.value)}
                  className="input-flat"
                  data-testid="invite-role-select">
                  <option value="city_official">Social Equity Department</option>
                  <option value="client">Applicant</option>
                </select>
              </div>
              <div className="flex-shrink-0 mt-6">
                <button type="submit" disabled={sending}
                  className="btn-primary h-10 px-6"
                  data-testid="send-invites-button">
                  {sending ? "Sending..." : "Send Invites"}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Invite list */}
        <div>
          <Eyebrow className="mb-4">All Invitations</Eyebrow>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-line border-t-equal rounded-full animate-spin" />
            </div>
          ) : invites.length === 0 ? (
            <div className="card-flat text-center py-16">
              <p className="text-mute text-sm">No invitations sent yet.</p>
            </div>
          ) : (
            <div className="card-flat overflow-hidden">
              {/* Header row */}
              <div className="grid grid-cols-12 px-5 py-3 border-b border-line th-mono">
                <span className="col-span-4">Email</span>
                <span className="col-span-2">Role</span>
                <span className="col-span-2">Status</span>
                <span className="col-span-2">Sent</span>
                <span className="col-span-2 text-right">Actions</span>
              </div>
              <div className="divide-y divide-line">
                {sortedInvites.map(invite => {
                  const s = STATUS_STYLES[invite.status] || STATUS_STYLES.pending;
                  return (
                    <div key={invite.id} className="grid grid-cols-12 px-5 py-4 items-center hover:bg-page transition-colors"
                      data-testid={`invite-row-${invite.id}`}>
                      <div className="col-span-4">
                        <p className="text-sm font-medium text-ink truncate">{invite.email}</p>
                        <p className="text-xs text-mute mt-0.5">Invited by {invite.invited_by}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-xs text-ink-2">{ROLE_LABELS[invite.role] || invite.role}</span>
                      </div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-md border ${s.cls}`}>
                          {s.label}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-mute font-mono">{new Date(invite.created_at).toLocaleDateString()}</p>
                        {invite.opened_at && <p className="text-xs text-[#1f5f8b] mt-0.5">Opened {new Date(invite.opened_at).toLocaleDateString()}</p>}
                        {invite.accepted_at && <p className="text-xs text-equal-dark mt-0.5">Accepted {new Date(invite.accepted_at).toLocaleDateString()}</p>}
                      </div>
                      <div className="col-span-2 flex justify-end gap-2">
                        {invite.status !== "accepted" && (
                          <button onClick={() => handleResend(invite.id, invite.email)}
                            disabled={resending === invite.id}
                            className="btn-ghost btn-sm h-7 px-2.5 text-equal-dark"
                            data-testid={`resend-${invite.id}`}>
                            {resending === invite.id ? "Sending..." : "Resend"}
                          </button>
                        )}
                        {userRole === "admin" && invite.status !== "accepted" && (
                          <button onClick={() => handleDelete(invite.id, invite.email)}
                            className="btn-ghost btn-sm h-7 px-2.5 text-mute hover:text-[#c0392b] hover:border-[#f2c1bb] hover:bg-[#fdecea]"
                            data-testid={`cancel-${invite.id}`}>
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
